import { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import { CircleNotch, DownloadSimple, WarningCircle } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useT } from '@/i18n/LanguageContext';
import { mfgApi } from './mfgApi';

const IMG_EXTS = ['png', 'jpg', 'jpeg', 'gif', 'webp'];
const SHEET_EXTS = ['xlsx', 'xls', 'csv'];

// In-app previewer for a single attachment: PDF (iframe), images, Excel/CSV (table).
export default function AttachmentViewer({ open, onClose, attachment }) {
  const { t } = useT();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [blobUrl, setBlobUrl] = useState('');
  const [sheets, setSheets] = useState(null); // [{ name, rows: [[..]] }]
  const [activeSheet, setActiveSheet] = useState(0);

  const ext = attachment?.ext;

  useEffect(() => {
    if (!open || !attachment) return;
    let url = '';
    setLoading(true); setError(false); setSheets(null); setActiveSheet(0); setBlobUrl('');
    mfgApi.attachmentBlob(attachment.id)
      .then(async (blob) => {
        if (SHEET_EXTS.includes(ext)) {
          const buf = await blob.arrayBuffer();
          const wb = XLSX.read(buf, { type: 'array' });
          const parsed = wb.SheetNames.map((name) => ({
            name,
            rows: XLSX.utils.sheet_to_json(wb.Sheets[name], { header: 1, blankrows: false, defval: '' }),
          }));
          setSheets(parsed);
        } else {
          url = URL.createObjectURL(blob);
          setBlobUrl(url);
        }
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [open, attachment, ext]);

  const download = async () => {
    try {
      const blob = await mfgApi.attachmentBlob(attachment.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = attachment.original_filename; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch { /* ignore */ }
  };

  const sheet = sheets?.[activeSheet];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl w-[95vw] max-h-[92vh] overflow-hidden flex flex-col" aria-describedby={undefined} data-testid="attachment-viewer">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 pr-8 min-w-0">
            <span className="truncate">{attachment?.original_filename}</span>
            <Button size="sm" variant="outline" className="h-7 text-xs ml-auto shrink-0" onClick={download} data-testid="viewer-download-btn">
              <DownloadSimple size={13} className="mr-1" /> {t('mfg.attach.download')}
            </Button>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-auto rounded-lg bg-zinc-50 border border-zinc-200">
          {loading ? (
            <div className="h-[60vh] flex items-center justify-center text-zinc-400" data-testid="viewer-loading">
              <CircleNotch size={28} className="animate-spin" />
            </div>
          ) : error ? (
            <div className="h-[60vh] flex flex-col items-center justify-center text-zinc-400 gap-2" data-testid="viewer-error">
              <WarningCircle size={30} /> <span className="text-sm">{t('mfg.attach.viewFailed')}</span>
            </div>
          ) : ext === 'pdf' ? (
            <iframe title={attachment?.original_filename} src={blobUrl} className="w-full h-[74vh] bg-white" data-testid="viewer-pdf" />
          ) : IMG_EXTS.includes(ext) ? (
            <div className="p-4 flex items-center justify-center" data-testid="viewer-image">
              <img src={blobUrl} alt={attachment?.original_filename} className="max-w-full max-h-[74vh] object-contain" />
            </div>
          ) : sheets ? (
            <div className="flex flex-col h-[74vh]" data-testid="viewer-sheet">
              {sheets.length > 1 && (
                <div className="flex gap-1 p-2 border-b border-zinc-200 bg-white overflow-x-auto shrink-0">
                  {sheets.map((s, i) => (
                    <button key={s.name} onClick={() => setActiveSheet(i)} data-testid={`viewer-sheet-tab-${i}`}
                      className={`px-3 py-1 rounded-md text-xs whitespace-nowrap ${i === activeSheet ? 'bg-orange-500 text-white' : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'}`}>
                      {s.name}
                    </button>
                  ))}
                </div>
              )}
              <div className="flex-1 overflow-auto">
                {(!sheet || sheet.rows.length === 0) ? (
                  <p className="p-6 text-center text-sm text-zinc-400">{t('mfg.attach.sheetEmpty')}</p>
                ) : (
                  <table className="text-xs border-collapse w-full">
                    <tbody>
                      {sheet.rows.map((row, ri) => (
                        <tr key={ri} className={ri === 0 ? 'bg-zinc-100 font-semibold sticky top-0' : 'even:bg-white odd:bg-zinc-50/50'}>
                          <td className="border border-zinc-200 px-2 py-1 text-zinc-300 text-[10px] bg-zinc-100 tabular-nums text-right select-none">{ri + 1}</td>
                          {row.map((cell, ci) => (
                            <td key={ci} className="border border-zinc-200 px-2 py-1 text-zinc-700 whitespace-nowrap max-w-[300px] truncate">{String(cell ?? '')}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          ) : (
            <div className="h-[60vh] flex items-center justify-center text-sm text-zinc-400">{t('mfg.attach.viewUnsupported')}</div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
